import { Logger } from './logger';
import { BehaviorSubject } from 'rxjs';
import { Grid } from 'gridjs';
import 'gridjs/dist/theme/mermaid.css';

export class Utils {
  public scrollPosition: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  constructor(private $log: Logger) {
    this.setScrollPostion();
    this.testfunction();
    this.windowResize();
    this.clickFlyoutPanelBackdrop();
  }

  /**
   * Sets the current scrollposition of the window
   * Populates the observer
   */
  private setScrollPostion(): void {
    window.addEventListener('scroll', () => {
        const offset = window.scrollY;
        this.scrollPosition.next(offset);
      }, { passive: true });
  }

  /**
   * Checks whether all images are loaded on the page
   * @returns a promise; true when all images are loaded
   */
  public async areAllImagesLoaded(): Promise<boolean> {
    return Promise.all(
      Array.from(document.images).map((img) => {
        if (img.complete) return Promise.resolve(img.naturalHeight !== 0);
        return new Promise((resolve) => {
          img.addEventListener('load', () => resolve(true));
          img.addEventListener('error', () => resolve(false));
        });
      })
    ).then((results) => {
        if (results.every((res) => res)) {
          this.$log.debug('Utiltiy: images are loaded', true);
          return true;
        } else {
          this.$log.debug('Utiltiy: not all images are loaded', true);
          return true;
        }
      }).catch((res) => {
        this.$log.debug('Utiltiy: images are not loaded', false);
        return false;
      });
  }

  /**
   * Measures the scrollbar witdth if there is any.
   * @returns the width of the scrollbar
   */
  public measureScrollbar(): number {
    const outerElement = document.createElement('div');
    outerElement.style.visibility = 'hidden';
    outerElement.style.overflow = 'scroll';
    document.body.appendChild(outerElement);

    const innerElement = document.createElement('div');
    outerElement.appendChild(innerElement);
    const scrollbarWidth = outerElement.offsetWidth - innerElement.offsetWidth;
    outerElement.parentNode.removeChild(outerElement);

    this.$log.debug('Utiltiy: ScrollbarWith', scrollbarWidth);

    return scrollbarWidth;
  }

  /**
   * Adds or removes a cals from one ore many elements
   * @param addOrRemove if the class should be added or removed
   * @param className the className to be added or removed
   * @param elements all the elements the class should be added or removed
   */
  public addOrRemoveClases(addOrRemove: 'add' | 'remove', className: string, ...elements: Array<HTMLElement | NodeListOf<HTMLElement>>): void {
    this.$log.debug('utilityService: addOrRemoveClass', addOrRemove, className, elements);
    elements?.forEach((element) => {
      if ('classList' in element) {
        this.setClasslist(addOrRemove, className, element);
      } else {
        element?.forEach((subElement) => {
          this.setClasslist(addOrRemove, className, subElement);
        });
      }
    });
  }

  /**
   * Handles the adding or removing of the class to the element.
   * @param addOrRemove if the class should be added or removed
   * @param className the className to be added or removed
   * @param element the element the class should be added or removed
   */
  private setClasslist(addOrRemove: 'add' | 'remove', className: string, element: HTMLElement): void {
    if (!element?.classList.contains(className) || addOrRemove === 'remove') {
      if (addOrRemove === 'add') {
        element?.classList.add(className);
      } else {
        element?.classList.remove(className);
      }
    }
  }

  /**
   * Initializes the gridjs table.
   * @param gridTable the table element which will be initialized
   */
  public initGridJsTable(gridTable: HTMLElement): Grid {
    const grid = new Grid({
      from: gridTable,
      sort: true,
      className: {
        table: 'u-table',
        th: 'u-table__th',
        tr: 'u-table__tr',
        td: 'u-table__td',
      },
    });

    const gridLimit: number = parseInt(gridTable.dataset.limit);

    if (gridLimit > 0) {
      grid.updateConfig({
        pagination: {
          limit: gridLimit,
        },
      });
    }

    grid.render(gridTable.nextElementSibling);

    return grid;
  }

  private testfunction(): void {
    const test: NodeListOf<HTMLElement> = document.querySelectorAll('.js-u-grid-table');

    test.forEach((element: HTMLElement) => {
      this.initGridJsTable(element);
    });
  }

  public windowResize(): void {
    window.addEventListener('resize', () => {
        document.dispatchEvent(new Event('base:navigation-close'));
      }, { passive: true });
  }

  /**
   * This function tells you, if the element will be within the viewport or not.
   * @param element HTMLElement, which will be checked.
   * @returns boolean
   */
  public isInViewport(element: HTMLElement, offset: number): boolean {
    const windowTopPosition = window.scrollY + offset;

    if (windowTopPosition <= element.offsetTop + element.offsetHeight) {
      if (element.offsetTop <= windowTopPosition) {
        return true;
      } else {
        return false;
      }
    }
  }

  public elementHeight(element: HTMLElement): number {
    return element.offsetHeight;
  }

  /**
   * Gets the current language from the html tag
   * @returns the language string
   */
  public getCurrentLanguage(): string {
    const html: HTMLElement = document.querySelector('html');
    const lang = html?.lang;

    return lang ?? 'en';
  }

  public activateFlyoutPanelBackdrop(): void {
    const panelBackdrop: HTMLElement = document.querySelector('.js-panel-backdrop');

    if (panelBackdrop) {
      panelBackdrop.dataset.active = 'true';
    }
  }

  public deactivateFlyoutPanelBackdrop(): void {
    const panelBackdrop: HTMLElement = document.querySelector('.js-panel-backdrop');
    const allOpenedElements: NodeListOf<HTMLElement> = document.querySelectorAll('.js-panel-backdrop-element');
    const backdropElement = document.querySelector('.js-panel-backdrop') as HTMLElement | null;
    if (backdropElement) {
      backdropElement.style.zIndex = '800';
    }
    allOpenedElements.forEach((openedElement: HTMLElement) => {
      this.addOrRemoveClases('remove', 'open', openedElement);
      this.addOrRemoveClases('remove', 'open', document.body);
      this.addOrRemoveClases('remove', 'js-panel-backdrop-element', openedElement);
    });
    panelBackdrop.dataset.active = 'false';
  }

  public clickFlyoutPanelBackdrop(): void {
    const panelBackdrop: HTMLElement = document.querySelector('.js-panel-backdrop');

    panelBackdrop.addEventListener('click', () => {
      this.deactivateFlyoutPanelBackdrop();
    });
  }
}
